import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { formatDoctorName } from "@/lib/doctorName";

// =====================================================================
// CompleteVisitDialog — the dentist's "tick chips, complete visit"
// modal. Drives Phase 4's complete_procedure_visit RPC.
//
// Pulls action_chips and default_materials from the catalog row's
// visit_template by sequence. Falls back to category_default_chips
// (loaded once on first open) when the procedure has no template.
// =====================================================================

export default function CompleteVisitDialog({
  open,
  onOpenChange,
  visit,
  item,
  procedure,
  defaultDoctorId = null,
  onCompleted,
}) {
  // Resolve the visit's template entry (action_chips, default_materials).
  const templateEntry = useMemo(() => {
    if (!procedure?.visit_template || !visit?.sequence) return null;
    if (!Array.isArray(procedure.visit_template)) return null;
    return procedure.visit_template.find((t) => Number(t.sequence) === Number(visit.sequence)) || null;
  }, [procedure, visit]);

  const [fallbackChips, setFallbackChips] = useState([]);
  const [doctorId, setDoctorId] = useState(defaultDoctorId);
  const [doctors, setDoctors]   = useState([]);
  const [tickedChips, setTickedChips] = useState({});
  const [materials, setMaterials]     = useState([]);
  const [notes, setNotes]             = useState("");
  const [submitting, setSubmitting]   = useState(false);

  // Load category fallback chips when the procedure has no inline template.
  useEffect(() => {
    if (!open) return;
    if (templateEntry) return;
    if (!procedure?.category) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("category_default_chips")
        .select("chips")
        .eq("category", procedure.category)
        .order("clinic_id", { ascending: true, nullsFirst: false })
        .limit(1);
      if (cancelled) return;
      const chips = Array.isArray(data?.[0]?.chips) ? data[0].chips : [];
      setFallbackChips(chips);
    })();
    return () => { cancelled = true; };
  }, [open, templateEntry, procedure?.category]);

  // Load doctors list (for the performed_by selector) on first open.
  useEffect(() => {
    if (!open || doctors.length > 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("doctors")
        .select("id, display_name")
        .eq("active", true)
        .order("display_name");
      if (cancelled) return;
      setDoctors(Array.isArray(data) ? data : []);
    })();
    return () => { cancelled = true; };
  }, [open, doctors.length]);

  // Reset form on open.
  useEffect(() => {
    if (!open) return;
    setDoctorId(defaultDoctorId || (doctors[0]?.id ?? null));
    setTickedChips({});
    setNotes("");
    // Default materials from the template entry (best signal).
    const defaults = Array.isArray(templateEntry?.default_materials)
      ? templateEntry.default_materials
      : Array.isArray(procedure?.default_materials)
        ? procedure.default_materials
        : [];
    setMaterials(defaults.map((stable) => ({ stable_id: stable, quantity: 1 })));
    setSubmitting(false);
  }, [open, templateEntry, procedure, defaultDoctorId, doctors]);

  const chipsForVisit = useMemo(() => {
    if (Array.isArray(templateEntry?.action_chips) && templateEntry.action_chips.length > 0) {
      return templateEntry.action_chips;
    }
    return fallbackChips;
  }, [templateEntry, fallbackChips]);

  if (!open || !visit || !item) return null;

  const totalVisits = item.visit_count_override ?? item.visit_count ?? 1;
  const isFinal = Number(visit.sequence) >= Number(totalVisits);

  const tickedList = chipsForVisit.filter((c) => tickedChips[c]);

  function toggleChip(chip) {
    setTickedChips((prev) => ({ ...prev, [chip]: !prev[chip] }));
  }

  function setMaterialQty(stableId, qty) {
    setMaterials((prev) => {
      const next = prev.filter((m) => m.stable_id !== stableId);
      const n = Number(qty);
      if (n > 0) next.push({ stable_id: stableId, quantity: n });
      return next;
    });
  }

  async function handleSubmit() {
    if (!doctorId) {
      toast.error("Pick the doctor performing this visit.");
      return;
    }
    setSubmitting(true);
    const chipLog = tickedList.map((label) => ({ label, ts: new Date().toISOString() }));
    const { data, error } = await supabase.rpc("complete_procedure_visit", {
      p_visit_id:         visit.id,
      p_performed_by:     doctorId,
      p_chip_log:         chipLog,
      p_materials_logged: materials,
      p_notes:            notes?.trim() || null,
      p_appointment_id:   null,
      p_visit_id_link:    null,
    });
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Could not complete visit.");
      return;
    }
    if (data?.is_final) {
      toast.success(`Final visit complete — procedure done.`);
    } else {
      toast.success(`Visit ${visit.sequence} complete.`);
    }
    onCompleted?.(data);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="complete-visit-title"
      data-testid="complete-visit-dialog"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <div>
            <h2 id="complete-visit-title" className="text-base font-semibold text-slate-900">
              Complete visit {visit.sequence} of {totalVisits}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {item.name_snapshot || procedure?.name || "Procedure"}
              {visit.template_label ? <span className="text-slate-700"> · {visit.template_label}</span> : null}
              {isFinal ? <span className="ml-2 text-emerald-700 font-semibold">final</span> : null}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            className="text-slate-400 hover:text-slate-700 -mt-1"
            aria-label="Close"
            data-testid="complete-visit-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <section>
            <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
              Performed by
            </p>
            <select
              value={doctorId || ""}
              onChange={(e) => setDoctorId(e.target.value || null)}
              data-testid="complete-visit-doctor"
              disabled={submitting || doctors.length === 0}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
            >
              <option value="">— Select doctor —</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>{formatDoctorName(d)}</option>
              ))}
            </select>
          </section>

          <section>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500">
                Action chips
              </p>
              <span className="text-[11px] text-slate-400">
                {tickedList.length} ticked
              </span>
            </div>
            {chipsForVisit.length === 0 ? (
              <p className="text-xs text-slate-500 italic">No chips defined for this visit — note + complete still works.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {chipsForVisit.map((chip) => {
                  const ticked = !!tickedChips[chip];
                  return (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => toggleChip(chip)}
                      data-testid={`complete-visit-chip-${chip}`}
                      disabled={submitting}
                      className={`px-2.5 py-1.5 text-xs font-medium rounded-lg border transition ${
                        ticked
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                      }`}
                    >
                      {ticked ? "✓ " : ""}{chip}
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          {Array.isArray(templateEntry?.default_materials) && templateEntry.default_materials.length > 0 ? (
            <section>
              <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
                Materials used
              </p>
              <div className="space-y-1.5">
                {templateEntry.default_materials.map((stable) => {
                  const cur = materials.find((m) => m.stable_id === stable);
                  return (
                    <div key={stable} className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={cur?.quantity ?? 0}
                        onChange={(e) => setMaterialQty(stable, e.target.value)}
                        disabled={submitting}
                        data-testid={`complete-visit-material-${stable}`}
                        className="w-16 px-2 py-1 text-sm border border-slate-300 rounded tabular-nums focus:outline-none focus:border-emerald-500"
                      />
                      <span className="text-xs text-slate-700">{stable}</span>
                    </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                Quantities default to 1 from the catalog template.
              </p>
            </section>
          ) : null}

          <section>
            <p className="text-[11px] font-bold tracking-wider uppercase text-slate-500 mb-2">
              Notes (optional)
            </p>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything unusual? Patient cooperation, surprises, etc."
              data-testid="complete-visit-notes"
              disabled={submitting}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
            />
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <button
            type="button"
            onClick={() => onOpenChange?.(false)}
            disabled={submitting}
            data-testid="complete-visit-cancel"
            className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !doctorId}
            data-testid="complete-visit-submit"
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isFinal ? "Complete & finish procedure" : "Complete visit"}
          </button>
        </div>
      </div>
    </div>
  );
}
